import React from 'react';
import PropTypes from 'prop-types';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { CookiesProvider } from 'react-cookie';
import { Provider as StoreProvider } from 'react-redux';
import { LightgalleryProvider } from 'react-lightgallery';
import { SnackbarProvider } from 'notistack';

import { ThemeProvider, createTheme, adaptV4Theme } from '@mui/material/styles';
import {
  ThemeProvider as LegacyThemeProvider,
  StylesProvider as V4StylesProvider,
  createGenerateClassName as createV4GenerateClassName
} from '@material-ui/core/styles';
import {
  StylesProvider,
  createGenerateClassName,
  ThemeProvider as MuiStylesThemeProvider
} from '@mui/styles';

import * as storage from '_core/utils/storage';
import { VisitorInfoProvider } from '_core/utils/visitorInfo';
import { DeliveryProvider } from '_core/utils/delivery';
import { getTheme } from '_core/themes/getTheme';
import { AnalyticsProvider } from '_core/contexts/Analytics';
import { CartProvider } from '_core/contexts/Cart';
import { ConfigProvider } from '_core/contexts/Config';
import { initStore } from '_core/store/store';

import { THEME_TYPE, STORAGE_KEY } from 'basic/config';

import { AppView } from './AppView';

import 'basic/assets/scss/_fileupload.scss';
import 'basic/assets/scss/scrollbar.scss';
import 'basic/assets/scss/screen.scss';
import 'basic/assets/scss/material.scss';
import 'basic/assets/scss/sbsans.scss';
import 'basic/assets/scss/manrope.scss';
import 'basic/assets/scss/involve.scss';
import 'basic/assets/scss/reformagrotesk.scss';

import 'lightgallery.js/dist/css/lightgallery.min.css';
import 'basic/assets/css/lightgallery.css';

import { StyledEngineProvider } from '@mui/material/styles';

const store = initStore();

const history = createBrowserHistory({
  basename: process.env.PUBLIC_URL
});

// Генератор классов для @mui/styles (v5 JSS)
const generateClassName = createGenerateClassName({
  productionPrefix: 'jss-v5',
  seed: 'mui5'
});

// Генератор классов для @material-ui/core (v4 JSS)
const generateV4ClassName = createV4GenerateClassName({
  productionPrefix: 'jss-v4',
  seed: 'mui4'
});

export const RawApp = (props) => {
  const { globalContext, render, Component, ...rest } = props;

  const {
    config: { SITE_ANALYTICS }
  } = globalContext;

  const [theme, setTheme] = React.useState();

  React.useEffect(() => {
    const themeMap = {};

    const applyTheme = (type) => {
      setTheme(themeMap[type] || themeMap.default);

      storage.set(STORAGE_KEY.THEME, type);
    };

    const themes = globalContext.themes;

    (themes?.length ? themes : [{ themeType: THEME_TYPE.LIGHT }]).forEach(
      (item) => {
        item = { ...getTheme(item), applyTheme };

        themeMap[item.themeType] = item;

        if (!themeMap.default) themeMap.default = item;
      }
    );

    applyTheme(storage.get(STORAGE_KEY.THEME));
  }, [globalContext.themes]);

  const mui5Theme = React.useMemo(() => {
    if (!theme) return undefined;

    const nextTheme = createTheme(adaptV4Theme(theme));

    nextTheme.applyTheme = theme.applyTheme;
    nextTheme.themeType = theme.themeType;
    nextTheme.themeName = theme.themeName;
    nextTheme.themeIcon = theme.themeIcon;
    nextTheme.variables = theme.variables;
    nextTheme.pages = theme.pages;
    nextTheme.utils = theme.utils;

    return nextTheme;
  }, [theme]);

  if (!mui5Theme) return null;

  const childProps = { globalContext, ...rest };

  return (
    <StyledEngineProvider injectFirst>
      <StylesProvider injectFirst generateClassName={generateClassName}>
        <ThemeProvider theme={mui5Theme}>
          <MuiStylesThemeProvider theme={mui5Theme}>
            <V4StylesProvider injectFirst generateClassName={generateV4ClassName}>
              <LegacyThemeProvider theme={theme}>
                <CookiesProvider>
                  <StoreProvider store={store}>
                    <SnackbarProvider maxSnack={3}>
                      <ConfigProvider>
                        <VisitorInfoProvider>
                          <DeliveryProvider>
                            <LightgalleryProvider>
                              <Router history={history}>
                                <AnalyticsProvider config={SITE_ANALYTICS}>
                                  <CartProvider>
                                    {typeof render === 'function' ? (
                                      render(childProps)
                                    ) : (
                                      <Component {...childProps} />
                                    )}
                                  </CartProvider>
                                </AnalyticsProvider>
                              </Router>
                            </LightgalleryProvider>
                          </DeliveryProvider>
                        </VisitorInfoProvider>
                      </ConfigProvider>
                    </SnackbarProvider>
                  </StoreProvider>
                </CookiesProvider>
              </LegacyThemeProvider>
            </V4StylesProvider>
          </MuiStylesThemeProvider>
        </ThemeProvider>
      </StylesProvider>
    </StyledEngineProvider>
  );
};

RawApp.defaultProps = {
  Component: AppView
};

RawApp.propTypes = {
  // self props
  globalContext: PropTypes.object.isRequired,
  render: PropTypes.func,
  Component: PropTypes.elementType
};

export const App = RawApp;
